"use client";

import React, { ReactNode, useState, useEffect, useRef } from "react";
import Section from "@/components/section";
import { Button, buttonVariants } from "@/components/ui/button";
import { motion } from "src/lib/motion";
import { cn } from "@/lib/utils";
import { Check, Loader2 } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { Icons } from "@/components/icons";
import { useTranslations } from "@/lib/hooks/use-translations";

// Import Bilder
import businessDiscussionImage from "@/assets/images/business-discussion.jpg";
import scientistImage from "@/assets/images/scientist-working-laboratory-analysing-test-trial-vaccine-checking-drug-data-standing-front-pc.jpg";
import smartphoneOfficeImage from "@/assets/images/smartphone-office.jpg";
import productionLineImage from "@/assets/images/photo-automobile-production-line-welding-car-body-modern-car-assembly-plant-auto-industry.jpg";
import logisticsImage from "@/assets/images/technological-futuristic-holograms-logistics-means-transport.jpg";
import clsx from "clsx";

const imageMap: Record<string, any> = {
  "@/assets/images/business-discussion.jpg": businessDiscussionImage,
  "@/assets/images/scientist-working-laboratory-analysing-test-trial-vaccine-checking-drug-data-standing-front-pc.jpg": scientistImage,
  "@/assets/images/smartphone-office.jpg": smartphoneOfficeImage,
  "@/assets/images/photo-automobile-production-line-welding-car-body-modern-car-assembly-plant-auto-industry.jpg": productionLineImage,
  "@/assets/images/technological-futuristic-holograms-logistics-means-transport.jpg": logisticsImage
};

export interface TabItem {
  id: string;
  label: string;
  icon?: ReactNode;
}

export interface TabContent {
  title: string;
  description: string;
  features: string[];
  image: string;
  url: string;
}

export interface TabSwitchSectionProps {
  title: string;
  subtitle: string;
  categories: TabItem[];
  contents: Record<string, TabContent>;
  defaultTab?: string;
  className?: string;
  titleClassName?: string;
  subtitleClassName?: string;
  navigationStyles?: string
}

export default function TabSwitchSection({
  title,
  subtitle,
  categories,
  contents,
  defaultTab,
  className,
  titleClassName = "text-sm text-primary font-mono font-medium tracking-wider mb-4 uppercase",
  subtitleClassName = "text-4xl md:text-5xl font-bold break-words",
  navigationStyles,
}: TabSwitchSectionProps) {
  const [activeTab, setActiveTab] = useState(defaultTab || categories[0]?.id || "");
  const [loadedTabs, setLoadedTabs] = useState<Record<string, boolean>>({});
  const [loadingTab, setLoadingTab] = useState<string | null>(null);
  const { t } = useTranslations();
  const content = contents[activeTab] || Object.values(contents)[0];
  
  // Combined tab loading and preloading mechanism
  useEffect(() => {
    if (typeof window === 'undefined' || !activeTab) return;
    
    const loadImage = async (tabId: string) => {
      if (!contents[tabId] || loadedTabs[tabId]) return;
      
      return new Promise<void>((resolve) => {
        const img = new window.Image();
        img.onload = () => {
          if (!loadedTabs[tabId]) {
            setLoadedTabs(prev => ({ ...prev, [tabId]: true }));
          }
          resolve();
        };
        img.src = contents[tabId].image;
      });
    };

    const loadTabsSequentially = async () => {
      // Load active tab first
      await loadImage(activeTab);
      
      // Determine next tabs for preloading
      const currentIndex = categories.findIndex(cat => cat.id === activeTab);
      const nextIndex = (currentIndex + 1) % categories.length;
      const nextNextIndex = categories.length > 3 ? (currentIndex + 2) % categories.length : -1;
      
      // Load next tabs in background
      if (nextIndex !== currentIndex) {
        await loadImage(categories[nextIndex].id);
      }
      
      if (nextNextIndex !== -1 && nextNextIndex !== currentIndex) {
        await loadImage(categories[nextNextIndex].id);
      }
    };

    loadTabsSequentially();
  }, [activeTab, categories, contents, loadedTabs]);
  
  // Tab change handler with loading indicator
  const handleTabChange = (tabId: string) => {
    if (tabId === activeTab) return;
    
    if (!loadedTabs[tabId]) {
      setLoadingTab(tabId);
    }
    
    setActiveTab(tabId);
  };

  return (
    <Section 
      className={className + " px-0"}
      title={title}
      subtitle={subtitle}
      titleClassName={titleClassName}
      subtitleClassName={subtitleClassName}
    >
      <div className="mx-auto max-w-7xl -mt-2">
        <div className={clsx("flex flex-wrap justify-center max-sm:gap-2 gap-3 mb-12", navigationStyles)}>
        {categories.map((category) => (
          <Button
            key={category.id}
            variant={activeTab === category.id ? "default" : "outline"}
            className={cn(
              "rounded-full px-6 mb-2 w-full sm:w-auto max-sm:w-fit max-sm:px-3",
              category.icon && "gap-2",
              activeTab === category.id 
                ? "bg-primary text-white border !border-primary" 
                : "bg-muted/30 hover:md:bg-primary/10"
            )}
            onClick={() => handleTabChange(category.id)}
          >
            {category.icon}
            {category.label}
          </Button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
        {/* Only render active tab content */}
        {Object.entries(contents).map(([tabId, tabContent]) => (
          tabId === activeTab && (
            <React.Fragment key={tabId}>
              {/* Left column: Text and Features */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                className="space-y-4"
              >
                <h3 className="text-2xl md:text-3xl font-semibold text-center sm:text-left">{tabContent.title}</h3>
                <p className="text-muted-foreground text-lg text-center sm:text-left">{tabContent.description}</p>
                
                <div className="space-y-3 mt-5 max-md:pb-6">
                  {tabContent.features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3 text-center sm:text-left">
                      <div className="mt-1 bg-primary/10 rounded-full p-1">
                        <Check className="h-4 w-4 text-primary" />
                      </div>
                      <p className="text-left">{feature}</p>
                    </div>
                  ))}
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4 mt-4">
                  <Link
                    href={t?.('common.navigation.contact') === 'Kontakt' ? '/de/kontakt' : '/contact'}
                    className={cn(
                      buttonVariants({ variant: "default" }),
                      "w-full sm:w-auto text-background flex gap-2"
                    )}
                  >
                    <Icons.logoIcon className="h-6 w-6" />
                    {t?.('pages.solutions.ai-consulting.hero.cta.primary') || 'Get Started for Free'}
                  </Link>
                  <button
                    onClick={() => document.getElementById("case-study")?.scrollIntoView({ behavior: "smooth" })}
                    className={cn(
                      buttonVariants({ variant: "outline" }),
                      "w-full sm:w-auto"
                    )}
                  >
                    {t?.('pages.solutions.ai-consulting.hero.cta.secondary') || 'Read More'}
                  </button>
                </div>
                
              </motion.div>
              
              {/* Right column: Screenshot */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                className="relative"
              >
                <div className={`w-full max-w-lg mx-auto rounded-lg overflow-hidden ${tabContent.image?.endsWith('.svg') ? 'bg-muted/30' : ''}`} style={{ height: '400px' }}>
                  {loadingTab === tabId && (
                    <div className="absolute inset-0 flex items-center justify-center bg-background/50 z-10">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                  )}
                  <Image
                    src={imageMap[tabContent.image]?.src || tabContent.image}
                    alt={`${tabContent.title} - Detailed visualization of ${tabContent.description.split('.')[0].toLowerCase()}`}
                    width={600}
                    height={400}
                    priority={tabId === categories[0]?.id} // Priority only for first tab image
                    loading={tabId === categories[0]?.id ? "eager" : "lazy"} // Eager loading only for first tab
                    onLoad={() => {
                      setLoadedTabs(prev => ({ ...prev, [tabId]: true }));
                      if (loadingTab === tabId) setLoadingTab(null);
                    }}
                    className={`w-full h-full ${tabContent.image?.endsWith('.svg') ? 'object-contain mix-blend-multiply dark:mix-blend-screen' : 'object-cover'}`}
                  />
                </div>
              </motion.div>
            </React.Fragment>
          )
        ))}
        </div>
      </div>
    </Section>
  );
}
